package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.AddressSearchException
import com.evoapps.evoshift.domain.model.AddressSearchFailure
import com.evoapps.evoshift.domain.model.AddressSuggestion
import com.evoapps.evoshift.domain.model.GeoLocation
import com.evoapps.evoshift.domain.repository.AddressSearchRepository
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlin.coroutines.cancellation.CancellationException

/**
 * The geocodeAddress callable (functions/geocode.js, SH-132). Places with a missing name or
 * coordinates out of range are skipped; refusals are mapped from `details.reason`, never the message.
 */
class AddressSearchRepositoryImpl(private val functions: CloudFunctionsClient) : AddressSearchRepository {

    override suspend fun search(workplaceId: String, query: String, language: String): Result<List<AddressSuggestion>> = try {
        val result = functions.call("geocodeAddress", buildJsonObject {
            put("workplaceId", workplaceId)
            put("query", query.trim())
            put("language", language)
        }) as? JsonObject
        val places = result?.get("results") as? JsonArray ?: JsonArray(emptyList())
        Result.success(places.mapNotNull { place ->
            val o = place as? JsonObject ?: return@mapNotNull null
            val label = o["label"]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() } ?: return@mapNotNull null
            val latitude = o["latitude"]?.jsonPrimitive?.doubleOrNull ?: return@mapNotNull null
            val longitude = o["longitude"]?.jsonPrimitive?.doubleOrNull ?: return@mapNotNull null
            GeoLocation.of(latitude, longitude)?.let { AddressSuggestion(label, it) }
        })
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        Result.failure(AddressSearchException(failureOf(e), e))
    } catch (e: Exception) {
        Result.failure(AddressSearchException(AddressSearchFailure.FAILED, e))
    }

    companion object {
        fun failureOf(e: CloudFunctionsException): AddressSearchFailure {
            val reason = (e.details as? JsonObject)?.get("reason")?.jsonPrimitive?.contentOrNull
            return when {
                reason == "BUSY" || e.code == "resource-exhausted" -> AddressSearchFailure.BUSY
                reason == "NOT_MANAGER" || reason == "NOT_SIGNED_IN" ||
                    e.code == "permission-denied" || e.code == "unauthenticated" -> AddressSearchFailure.NOT_ALLOWED
                else -> AddressSearchFailure.FAILED
            }
        }
    }
}
